import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import MagneticButton from './MagneticButton';

const NAV_ITEMS = [
  { id: 'hero', label: 'Home' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'currently', label: 'Currently' },
  { id: 'contact', label: 'Contact' },
];

export default function FloatingNav() {
  const [active, setActive] = useState('hero');
  const [visible, setVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Clear any previous theme overrides to keep pure light theme
    localStorage.removeItem('darshni-theme');
    document.documentElement.removeAttribute('data-theme');

    const updateNav = () => {
      const isHome = location.pathname === '/';
      // Always visible on case studies / subpages so user has immediate access to navigation;
      // on home page, appears after scrolling past 150px.
      setVisible(!isHome || window.scrollY > 150);

      if (!isHome) {
        setActive('projects');
        return;
      }

      const sections = NAV_ITEMS.map((item) => ({
        id: item.id,
        el: document.getElementById(item.id),
      })).filter((s) => s.el);

      let current = 'hero';
      for (const s of sections) {
        const rect = s.el.getBoundingClientRect();
        if (rect.top <= 160) current = s.id;
      }
      setActive(current);
    };

    updateNav();
    window.addEventListener('scroll', updateNav, { passive: true });
    return () => window.removeEventListener('scroll', updateNav);
  }, [location.pathname]);

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      if (id === 'hero') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <>
      <style>{`
        .desktop-nav {
          display: flex;
          align-items: center;
          gap: 0.15rem;
        }
        .mobile-nav-toggle {
          display: none;
        }
        @media (max-width: 860px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-nav-toggle {
            display: flex !important;
          }
        }
      `}</style>

      <AnimatePresence>
        {visible && (
          <motion.nav
            initial={{ y: -30, opacity: 0, x: '-50%' }}
            animate={{ y: 0, opacity: 1, x: '-50%' }}
            exit={{ y: -30, opacity: 0, x: '-50%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed',
              top: '1.25rem',
              left: '50%',
              zIndex: 9000,
              background: 'rgba(250, 246, 238, 0.85)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid var(--border)',
              boxShadow: 'inset 0 1px 0 var(--glass-specular), var(--shadow-md)',
              borderRadius: '100px',
              padding: '0.4rem 0.6rem 0.4rem 1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              maxWidth: 'calc(100vw - 2rem)',
            }}
          >
            {/* Monogram Logo */}
            <button
              onClick={() => scrollTo('hero')}
              data-cursor-hover
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: 'var(--text)',
                letterSpacing: '-0.02em',
                paddingRight: '0.5rem',
                borderRight: '1px solid var(--border)',
              }}
            >
              <span>D·S</span>
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#3a7050',
                  boxShadow: '0 0 6px #3a7050',
                  animation: 'pulse-soft 2s infinite',
                }}
              />
            </button>

            {/* Desktop Navigation Links */}
            <div className="desktop-nav">
              {NAV_ITEMS.map((item) => {
                const isCurrent = active === item.id;
                return (
                  <MagneticButton key={item.id} strength={0.25}>
                    <button
                      onClick={() => scrollTo(item.id)}
                      data-cursor-hover
                      style={{
                        position: 'relative',
                        padding: '0.4rem 0.85rem',
                        borderRadius: '100px',
                        fontSize: '0.82rem',
                        fontWeight: isCurrent ? 600 : 500,
                        color: isCurrent ? '#ffffff' : 'var(--text-muted)',
                        transition: 'color 0.25s ease',
                        zIndex: 1,
                      }}
                    >
                      {isCurrent && (
                        <motion.span
                          layoutId="nav-active-pill"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'var(--text)',
                            borderRadius: '100px',
                            zIndex: -1,
                            boxShadow: '0 2px 8px rgba(45, 23, 83, 0.2)',
                          }}
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        />
                      )}
                      {item.label}
                    </button>
                  </MagneticButton>
                );
              })}
            </div>

            {/* Mobile Toggle Button */}
            <div className="mobile-nav-toggle" style={{ alignItems: 'center', gap: '0.4rem' }}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle Navigation Menu"
                style={{
                  padding: '0.35rem 0.65rem',
                  borderRadius: '100px',
                  background: mobileMenuOpen ? 'var(--text)' : 'rgba(45, 23, 83, 0.08)',
                  color: mobileMenuOpen ? '#fff' : 'var(--text)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                }}
              >
                {mobileMenuOpen ? '✕' : '☰'}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed',
              top: '4.5rem',
              left: '1rem',
              right: '1rem',
              zIndex: 8999,
              background: 'rgba(250, 246, 238, 0.95)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid var(--border)',
              borderRadius: '24px',
              padding: '1.25rem',
              boxShadow: 'var(--shadow-lg)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
            }}
          >
            {NAV_ITEMS.map((item) => {
              const isCurrent = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.8rem 1.1rem',
                    borderRadius: '14px',
                    fontSize: '0.95rem',
                    fontWeight: isCurrent ? 600 : 500,
                    color: isCurrent ? '#ffffff' : 'var(--text)',
                    background: isCurrent ? 'var(--text)' : 'transparent',
                    textAlign: 'left',
                  }}
                >
                  <span>{item.label}</span>
                  {isCurrent && <span style={{ fontSize: '0.8rem', color: 'var(--accent-soft)' }}>●</span>}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
